import React, { useState, useEffect, useMemo } from 'react';
import {
	View, ScreenSpinner, AdaptivityProvider,
	AppRoot, ConfigProvider, SplitLayout,
	PanelHeader, SplitCol, Root, Alert, ModalRoot, Link, Snackbar,
} from '@vkontakte/vkui';
import '@vkontakte/vkui/dist/vkui.css';
import HttpClient from './HttpClient';
import { Pages, Panels, Views } from './panels/Consts';
import CreatePaymentPanel from './panels/Items/CreatePaymentPanel';
import CreateOnePaymentFromQrCodePanel from './panels/Items/CreateOnePaymentFromQrCodePanel';
import CreateRepaymentPanel from './panels/Items/CreateRepaymentPanel';
import ShowRepaymentPanel from './panels/Items/ShowRepaymentPanel';
import EditRepaymentPanel from './panels/Items/EditRepaymentPanel';
import ProfilePanel from './panels/ProfilePanel';
import InvitePanel from './panels/InvitePanel';
import ShowEventPanel from './panels/Events/ShowEventPanel';
import ShowPaymentPanel from './panels/Items/ShowPaymentPanel';
import CreateEventModal from './panels/Events/CreateEventModal';
import EditPaymentPanel from './panels/Items/EditPaymentPanel';
import EventSettingsPanel from './panels/Events/EventSettingsPanel';
import CreateManyPaymentsFromQrCodePanel from './panels/Items/CreateManyPaymentsFromQrCodePanel';
import EventsListPanel from './panels/Events/EventsListPanel';
import OnBoardingModal from './panels/OnBoardingModal';

import { Lang } from './Langs';
import { useRouteNavigator, useActiveVkuiLocation, useGetPanelForView, useFirstPageCheck, useMetaParams, useParams } from '@vkontakte/vk-mini-apps-router';
import { AppContextProvider } from './AppContext';
import { Modals, VK_APP_ID } from './Consts';
import * as Sentry from '@sentry/browser';
import { getResponseFromError } from './helpers/helpers';
import Interpolate from '@doist/react-interpolate';
import { TelegramBridge, VkBridge } from './Bridges';
import { useAppStore } from './AppStore';
import packageJson from '../package.json';
import { getRouteEventsList } from './route';
import MockClient from './MockClient';
import { BuyReceiptScansModal } from './modals/BuyReceiptScansModal';
import { BuyEventsModal } from './modals/BuyEventsModal';
import ScanReceiptsListPanel from './panels/Events/ScanReceiptsListPanel';
import ScanReceiptPanel from './panels/Events/ScanReceiptPanel';
import { ScanReceiptModal } from './panels/Events/components/ScanReceiptButtonComponent';
import EventsArchiveListPanel from './panels/Events/EventsArchiveListPanel';

export const APP_PLATFORM_TYPE_VK = 'vk';
export const APP_PLATFORM_TYPE_TELEGRAM = 'telegram';

const App = ({appOpenParams, platform}) => {
	const router = useRouteNavigator();
	const isFirstPageCheck = useFirstPageCheck();
	const appStore = useAppStore();

	const { view: activeView, modal: activeModal } = useActiveVkuiLocation();
	const activePanel = useGetPanelForView(Panels.EVENTS_LIST);

	const [popout, setPopout] = useState(null);
	const [cache, setCache] = useState({});
	const [colorScheme, setColorScheme] = useState('light');
	const [snackbar, setSnackbar] = useState(null);

	const httpClient = useMemo(() => {
		if (appOpenParams.host !== 'mock') {
			return new HttpClient('https://' + (appOpenParams.host || 'api.who2who.top'));
		}

		return new MockClient('');
	}, []);

	const AppContext = {
		appOpenParams,
		cache: {
			get(key) {
				return cache[key];
			},
			set(key, data) {
				cache[key] = data;
				setCache({...cache})
			},
		},
		platform,
		store: appStore,
		spinner: (show = true) => {
			setPopout(show ? <ScreenSpinner size='large'/> : null);
		},
		snackbar: snackbar,
		setSnackbar: ({text, duration = 5000, action = null, onActionClick = null, before = null}) => {
			setSnackbar(<Snackbar
				duration={duration}
				before={before}
				action={action}
				onActionClick={onActionClick}
				onClose={() => setSnackbar(null)}
			>
				{text}
			</Snackbar>);
		},
		showError: (error, message = null, header = null, onClose = () => {}, delay = 60) => {
			if (!message && error) {
				if ((error?.error_data?.error_code || null) === 6) {
					message = AppContext.lang().getKey('errors', 'platformNotSupported');
				} else if (error?.response?.status === 401) {
					message = AppContext.lang().getKey('errors', 'accessDenied');
				} else if (error?.response?.status === 404) {
					message = AppContext.lang().getKey('errors', 'notFound');
				} else if (error?.response?.status === 422) {
					message = AppContext.lang().getKey('errors', 'dataValidation');
				} else if (error?.response?.status === 426) {
					switch (platform) {
						case APP_PLATFORM_TYPE_VK:
							message = (<Interpolate
								string={AppContext.lang().getKey('errors', 'upgradeRequired')}
								mapping={{
									link_web_m: (<Link href={'https://vk.com/app' + VK_APP_ID} target="_blank">vk.com/app{VK_APP_ID}</Link>)
								}}
							/>);
							break;
						case APP_PLATFORM_TYPE_TELEGRAM:
							message = AppContext.lang().getKey('errors', 'upgradeRequiredTelegram');
							break;
					}
				} else if (error?.response?.status === 429) {
					message = AppContext.lang().getKey('errors', 'tooManyAttempts');
					delay = 60;
				} else if (error?.response?.status === 500) {
					message = AppContext.lang().getKey('errors', 'unknownError');
				} else if (
					error?.message.toString().toLocaleLowerCase() === 'network error' ||
					error?.message.toString().toLocaleLowerCase() === 'timeout of 3000ms exceeded'
				) {
					message = AppContext.lang().getKey('errors', 'networkError');
				} else {
					const response = getResponseFromError(error);
					if (response && response?.message) {
						message = response.message
					} else {
						Sentry.captureException(error);
						message = error?.error_data?.error_reason || error?.message || error || AppContext.lang().getKey('errors', 'unknownError');
					}
				}
			}

			if (error) {
				console.error(error);
			}

			const timeout = setTimeout(() => {
				AppContext.popout(null);
				onClose();
			}, delay * 1000);

			AppContext.popout(<Alert
				onClose={() => {
					AppContext.popout(null);
					clearTimeout(timeout);
					onClose();
				}}
				header={header || AppContext.lang().getKey('app', 'error_modal_title')}
				text={message}
			/>);

			return true;
		},
		alert: (message, header = undefined, onClose = () => {}) => {
			AppContext.popout(<Alert
				onClose={() => {
					AppContext.popout(null);
					onClose();
				}}
				header={header}
				text={message}
			/>);
		},
		confirm: (message, callback, labelOk, labelCancel, isVertical = false) =>  {
			AppContext.popout(<Alert
				actionsLayout={isVertical ? 'vertical' : 'horizontal'}
				actions={[
					{
						title: labelOk,
						mode: 'destructive',
						action: () => callback(true),
					},
					{
						title: labelCancel,
						mode: 'cancel',
						action: () => callback(false),
					},
				]}
				onClose={() => AppContext.popout(null)}
				text={message}
			/>);
		},
		popout: setPopout,
		colorScheme,
		setColorScheme,
		back: () => {
			AppContext.popout(null);

			if (isFirstPageCheck) {
				AppContext.router.push(...getRouteEventsList());
			} else {
				AppContext.router.back();
			}
		},
		getBridge: () => {
			switch (platform) {
				case APP_PLATFORM_TYPE_VK:
					return VkBridge;
				case APP_PLATFORM_TYPE_TELEGRAM:
					return TelegramBridge;
			}

			throw 'Bridge is undefined';
		},
		router: {
			push: (to, params = {}, options = null) => router.push(to, params, options),
			replace: (to, params = {}, options = null) => router.replace(to, params, options),
			showModal: (id) => router.showModal(id),
			hideModal: () => router.hideModal(),
			getParams: () => ({...useParams(), ...useMetaParams()}),
			backTo: (to) => {
				return new Promise((resolve, reject) => {
					const panelsHistory = (router?.viewHistory?.panelsHistory || []).reverse();
					let step = 0;
					for (const i in panelsHistory) {
						const panelName = panelsHistory[i];
						if (panelName === to) {
							router.go(step);
							return resolve();
						}

						step--;
					}

					return reject(AppContext.router);
				});
			},
			backToRoot: () => router.backToFirst(),
			back: () => router.back(),
			getHistory: () => (router?.viewHistory?.panelsHistory || []),
			isFirstPageCheck,
		},
		init: (skipOnBoarding = false) => {
			return new Promise((resolve, reject)  => {
				if (AppContext.httpClient().getToken() !== null) {
					Sentry.setUser({
						id: AppContext.store.currentUserId,
					});

					if (!skipOnBoarding && !AppContext.store.passedOnBoarding) {
						AppContext.getBridge().runOnBoarding(AppContext);
					}

					return resolve({
						token: AppContext.httpClient().getToken(),
						user_id: AppContext.store.currentUserId,
						max_image_size: AppContext.store.maxFileSize,
						language_code: Lang.getLang(),
						onboarding: AppContext.passedOnBoarding,
					});
				}

				const httpInitCallback = (response) => {
					const token = response.data.token || false;
					if (token !== false) {
						Sentry.setUser({id: response.data.user_id});

						AppContext.httpClient().setToken(response.data.token);
						AppContext.httpClient().setLang(response.data.lang);

						AppContext.lang().setLang(response.data.lang);

						AppContext.store.setCurrentUserId(response.data.user_id);
						AppContext.store.setPassedOnBoarding(response.data.onboarding);
						AppContext.store.setMaxFileSize(response.data.max_image_size);
						AppContext.store.setAddToChatEnabled(response.data?.add_to_chat_enabled || false);
						AppContext.store.setGenerateEventImageEnabled(response.data?.generate_event_image_enabled || false);
						AppContext.store.setIsShowCheckQrCodeScan(response.data?.show_check_qr_code_scan || false);
						AppContext.store.setIsEnabledScanReceipt(response.data?.enabled_scan_receipt || false);

						AppContext.getBridge().retargeting.init(response.data.user_id);

						if (!skipOnBoarding && !response.data.onboarding) {
							AppContext.getBridge().runOnBoarding(AppContext);
						}

						return resolve({
							token: response.data.token,
							user_id: response.data.user_id,
							max_image_size: response.data.max_image_size,
							language_code: appOpenParams.vk_language,
							onboarding: response.data.onboarding,
						});
					}

					return reject(response.data);
				};

				const initData = AppContext.getBridge().getInitData(appOpenParams, packageJson.version);

				switch (platform) {
					case APP_PLATFORM_TYPE_VK:
						AppContext.httpClient().initUserVk(initData).then(httpInitCallback).catch((error) => reject(error));
						break;
					case APP_PLATFORM_TYPE_TELEGRAM:
						AppContext.httpClient().initUserTelegram(initData).then(httpInitCallback).catch((error) => reject(error));
						break;
					default:
						reject({
							message: 'platform is undefined',
						});
				}
			});
		},
		httpClient: () => httpClient,
		lang: () => Lang,
		locale: () => AppContext.lang().getLang(),
	};

	window.copyToClipboard = (textToCopy, textToNotify) => {
		AppContext.getBridge().copyToClipboard(textToCopy, () => {
			AppContext.setSnackbar({text: textToNotify});
			AppContext.getBridge().hapticFeedback.success();
		});
	};

	useEffect(() => {
		AppContext.getBridge().init(AppContext, appOpenParams);
	}, []);

	const modal = <ModalRoot
		activeModal={activeModal}
		onClose={() => router.hideModal()}
	>
		<CreateEventModal id={Modals.CREATE_EVENT_MODAL}/>
		<OnBoardingModal id={Modals.ON_BOARDING_MODAL} dynamicContentHeight={true}/>
		<BuyReceiptScansModal id={Modals.BUY_RECEIPT_SCANS_MODAL}/>
		<BuyEventsModal id={Modals.BUY_EVENTS_MODAL}/>
		<ScanReceiptModal id={Modals.SCAN_RECEIPT_MODAL}/>
	</ModalRoot>;

	return (
		<ConfigProvider
			isWebView
			scheme="inherit"
			locale={AppContext.locale()}
			transitionMotionEnabled={false}
			appearance={AppContext.colorScheme}
			hasCustomPanelHeaderAfter={false}
		>
			<AdaptivityProvider sizeY={'compact'}>
				<AppRoot mode={'full'}>
					<AppContextProvider value={AppContext}>
						<SplitLayout
							header={<PanelHeader delimiter="none"/>}
							modal={modal}
							popout={popout}
						>
							<SplitCol
								key="main"
								width="100%"
								animate={true}
							>
								<Root activeView={activeView}>
									<View
										nav={Views.MAIN}
										activePanel={activePanel}
									>
										<EventsListPanel id={Panels.EVENTS_LIST} />
										<EventsArchiveListPanel id={Panels.EVENTS_ARCHIVE_LIST} />
										<ShowEventPanel id={Panels.EVENT_INFO} />
										<EventSettingsPanel id={Panels.EVENT_SETTINGS} />
										<ScanReceiptsListPanel id={Panels.SCAN_RECEIPTS_LIST_PANEL} />
										<ScanReceiptPanel id={Panels.SCAN_RECEIPT_PANEL} />
										<CreatePaymentPanel id={Panels.CREATE_PAYMENT_PANEL} />
										<CreateOnePaymentFromQrCodePanel id={Panels.CREATE_PAYMENT_FROM_QR_CODE_PANEL} />
										<CreateManyPaymentsFromQrCodePanel id={Panels.CREATE_PAYMENTS_FROM_QR_CODE_PANEL} />
										<CreateRepaymentPanel id={Panels.CREATE_REPAYMENT_PANEL} />
										<ShowPaymentPanel id={Panels.PAYMENT_INFO_PANEL} />
										<ShowRepaymentPanel id={Panels.REPAYMENT_INFO_PANEL} />
										<EditPaymentPanel id={Panels.EDIT_PAYMENT_PANEL} />
										<EditRepaymentPanel id={Panels.EDIT_REPAYMENT_PANEL} />
										<ProfilePanel id={Panels.PROFILE_PANEL} />
										<InvitePanel id={Panels.INVITE_PANEL} />
									</View>
								</Root>
							</SplitCol>
						</SplitLayout>
					</AppContextProvider>
				</AppRoot>
			</AdaptivityProvider>
		</ConfigProvider>
	);
}

export default App;